import React, { useState, useEffect, useRef } from 'react';
import { api } from '../../services/api';
import { useLang } from '../../i18n/context';
import { useAuth } from '../../contexts/AuthContext';
import type { CityRadar } from '../../types';


// City flag emojis
const FLAGS: Record<string, string> = {
  DE: '\uD83C\uDDE9\uD83C\uDDEA', HU: '\uD83C\uDDED\uD83C\uDDFA',
  FR: '\uD83C\uDDEB\uD83C\uDDF7', ES: '\uD83C\uDDEA\uD83C\uDDF8',
  BE: '\uD83C\uDDE7\uD83C\uDDEA', NL: '\uD83C\uDDF3\uD83C\uDDF1',
  GB: '\uD83C\uDDEC\uD83C\uDDE7', IT: '\uD83C\uDDEE\uD83C\uDDF9',
};

const G = '#4caf50';
const GOLD = '#c9a84c';
const RED = '#e53935';

// GLOBAL Boom Zoom scan cooldown — mirrors the server's 48h limit, which is
// shared across ALL cities (not per-radar): scanning any city starts the clock
// for every city, so the admin can't city-hop to bypass it / exhaust the scan
// budget. Computed from the most recent last_scanned across all radars.
const SCAN_COOLDOWN_MS = 48 * 60 * 60 * 1000;
function globalScanCooldown(radars: { last_scanned?: string | null }[]): { cooling: boolean; hoursLeft: number } {
  let mostRecent = 0;
  for (const r of radars) {
    if (!r.last_scanned) continue;
    const t = new Date(r.last_scanned + (r.last_scanned.includes('Z') ? '' : 'Z')).getTime();
    if (!isNaN(t) && t > mostRecent) mostRecent = t;
  }
  if (!mostRecent) return { cooling: false, hoursLeft: 0 };
  const elapsed = Date.now() - mostRecent;
  if (elapsed >= SCAN_COOLDOWN_MS) return { cooling: false, hoursLeft: 0 };
  return { cooling: true, hoursLeft: Math.ceil((SCAN_COOLDOWN_MS - elapsed) / 3_600_000) };
}

// ─── Shared radar renderers (admin + public read-only views) ─────────────────

function RadarEventItem({ evt, locale }: { evt: any; locale: string }) {
  return (
    <div style={{ padding: '10px 0', borderBottom: '1px solid var(--border-color)' }}>
      <div style={{ fontFamily: 'JetBrains Mono', fontSize: '11px', color: G, marginBottom: '3px' }}>
        {new Date(evt.date).toLocaleDateString(locale, { weekday: 'short', day: 'numeric', month: 'short' })}
        {evt.start_time && ` · ${evt.start_time}`}
      </div>
      <div style={{ fontWeight: 700, fontSize: '13px', marginBottom: '3px' }}>{evt.title}</div>
      <div style={{ fontSize: '11px', color: 'var(--text-muted)', display: 'flex', gap: '10px' }}>
        {(evt.venue_name || evt.venue_address) && <span>📍 {evt.venue_name || evt.venue_address}</span>}
        {evt.city && <span>🏙️ {evt.city}</span>}
      </div>
      {evt.genre_tags?.length > 0 && (
        <div className="genre-tags" style={{ marginTop: '4px' }}>
          {evt.genre_tags.map((g: string) => (
            <span key={g} className={`genre-tag ${g.replace(/\s+/g, '-')}`}>{g}</span>
          ))}
        </div>
      )}
      {evt.source_url && (
        <a href={evt.source_url} target="_blank" rel="noopener noreferrer"
          style={{ fontSize: '10px', color: GOLD, textDecoration: 'none', marginTop: '4px', display: 'inline-block' }}>
          Source ↗
        </a>
      )}
    </div>
  );
}

function RadarEventList({ detail, locale, t }: { detail: any; locale: string; t: (k: string) => string }) {
  const now = new Date();
  const weekEnd = new Date(now.getTime() + 7 * 24 * 60 * 60 * 1000);
  const evs: any[] = detail?.events || [];
  const thisWeek = evs.filter(e => { const d = new Date(e.date); return d >= now && d <= weekEnd; });
  const later = evs.filter(e => { const d = new Date(e.date); return d > weekEnd; });
  return (
    <div style={{
      background: 'var(--bg-card)', border: '1px solid var(--border-color)', borderRadius: '8px',
      padding: '16px', maxHeight: '400px', overflowY: 'auto',
    }}>
      {thisWeek.length > 0 && (
        <div style={{ marginBottom: '16px' }}>
          <div style={{
            fontSize: '12px', fontWeight: 700, color: GOLD, textTransform: 'uppercase',
            letterSpacing: '2px', marginBottom: '10px', borderBottom: '1px solid rgba(201,168,76,0.2)', paddingBottom: '6px',
          }}>
            {t('sidebar.this_week')} ({thisWeek.length})
          </div>
          {thisWeek.map((evt: any) => <RadarEventItem key={evt.id} evt={evt} locale={locale} />)}
        </div>
      )}
      {later.length > 0 && (
        <div>
          <div style={{
            fontSize: '12px', fontWeight: 700, color: 'var(--text-muted)', textTransform: 'uppercase',
            letterSpacing: '1px', marginBottom: '10px',
          }}>
            {t('agenda.upcoming')} ({later.length})
          </div>
          {later.map((evt: any) => <RadarEventItem key={evt.id} evt={evt} locale={locale} />)}
        </div>
      )}
      {thisWeek.length === 0 && later.length === 0 && (
        <div style={{ color: 'var(--text-muted)', fontSize: '13px', fontStyle: 'italic', padding: '20px 0' }}>
          {t('radar.no_events')}
        </div>
      )}
    </div>
  );
}

function RadarMap({ data, locale }: { data: any; locale: string }) {
  const mapRef = useRef<HTMLDivElement>(null);
  const instRef = useRef<any>(null);
  useEffect(() => {
    if (!data || !mapRef.current || typeof (window as any).L === 'undefined') return;
    const L = (window as any).L;
    if (instRef.current) { instRef.current.remove(); instRef.current = null; }
    const map = L.map(mapRef.current, { zoomControl: false, attributionControl: false })
      .setView([data.latitude, data.longitude], 11);
    L.tileLayer('https://{s}.basemaps.cartocdn.com/dark_all/{z}/{x}/{y}{r}.png', { maxZoom: 18 }).addTo(map);
    L.circle([data.latitude, data.longitude], {
      radius: (data.radius_km || 30) * 1000, color: GOLD, fillColor: GOLD, fillOpacity: 0.04, weight: 1, dashArray: '8 4',
    }).addTo(map);
    L.circleMarker([data.latitude, data.longitude], {
      radius: 4, color: GOLD, fillColor: GOLD, fillOpacity: 0.8, weight: 0,
    }).addTo(map);
    for (const evt of (data.events || [])) {
      if (!evt.latitude || !evt.longitude) continue;
      const marker = L.circleMarker([evt.latitude, evt.longitude], {
        radius: 6, color: G, fillColor: G, fillOpacity: 0.7, weight: 1,
      }).addTo(map);
      marker.bindPopup(`
        <div style="font-family: 'Space Grotesk', sans-serif; min-width: 200px;">
          <div style="font-weight: 700; font-size: 13px; margin-bottom: 4px;">${evt.title}</div>
          <div style="font-size: 11px; color: #a0a0b0; margin-bottom: 4px;">
            ${new Date(evt.date).toLocaleDateString(locale, { weekday: 'short', day: 'numeric', month: 'short' })}
          </div>
          ${evt.venue_name ? `<div style="font-size: 11px;">📍 ${evt.venue_name}</div>` : ''}
          ${evt.source_url ? `<div style="margin-top: 6px;"><a href="${evt.source_url}" target="_blank" style="color: ${GOLD}; font-size: 10px;">Source ↗</a></div>` : ''}
        </div>
      `, { className: 'radar-popup' });
    }
    instRef.current = map;
    return () => { if (instRef.current) { instRef.current.remove(); instRef.current = null; } };
  }, [data, locale]);
  return (
    <div style={{
      background: 'var(--bg-card)', border: '1px solid var(--border-color)', borderRadius: '8px',
      overflow: 'hidden', minHeight: '400px',
    }}>
      <div ref={mapRef} style={{ width: '100%', height: '400px' }} />
    </div>
  );
}

/** Short explainer so visitors understand what Boom Zoom City is, and that it's
 *  still being built out. Shown above the public radar view. */
function BoomZoomExplainer({ t }: { t: (k: string) => string }) {
  return (
    <div style={{
      padding: '12px 16px', marginBottom: '16px', borderRadius: '8px',
      background: 'rgba(201,168,76,0.06)', border: '1px solid rgba(201,168,76,0.2)',
    }}>
      <div style={{ fontSize: '13px', color: 'var(--text-color)', lineHeight: '1.6' }}>
        {t('radar.explainer')}
      </div>
      <div style={{ fontSize: '12px', color: '#c9a84c', marginTop: '6px', fontWeight: 600 }}>
        🚧 {t('radar.wip')}
      </div>
    </div>
  );
}

/** Public read-only Boom Zoom view: any visitor sees the active city's events
 *  + map. City selection, scanning, IG focus, sources and add-city stay
 *  admin-only (those live in CityRadarAdmin). Single-active is enforced
 *  server-side, so there is normally exactly one active city to show. */
function CityRadarPublic() {
  const { t, lang } = useLang();
  const locale = lang === 'nl' ? 'nl-BE' : lang === 'en' ? 'en-GB' : 'fr-BE';
  const [detail, setDetail] = useState<any>(null);
  const [radar, setRadar] = useState<CityRadar | null>(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    (async () => {
      try {
        const radars = await api.getRadars();
        // Prefer a featured city, else the first active one.
        const active = radars.find((r: any) => r.is_active && r.ig_discovery_enabled)
          || radars.find((r: any) => r.is_active);
        if (active) {
          setRadar(active);
          setDetail(await api.getRadar(active.id));
        }
      } catch (err) {
        console.error('Failed to load radar:', err);
      } finally {
        setLoading(false);
      }
    })();
  }, []);

  if (loading) return <div className="loading"><span className="spinner"></span></div>;
  if (!radar) {
    return (
      <div>
        <div className="section-header"><span className="section-title">{t('radar.title')}</span></div>
        <BoomZoomExplainer t={t} />
        <div className="empty-state" style={{ padding: '40px' }}>
          <p style={{ fontSize: '14px', color: 'var(--text-muted)' }}>{t('radar.no_active_city')}</p>
        </div>
      </div>
    );
  }

  return (
    <div>
      <div className="section-header"><span className="section-title">{t('radar.title')}</span></div>
      <BoomZoomExplainer t={t} />
      <div style={{
        display: 'flex', alignItems: 'center', gap: '12px',
        padding: '12px 16px', marginBottom: '16px',
        background: 'var(--bg-card)', border: '1px solid var(--border-color)', borderRadius: '8px',
      }}>
        <span style={{ fontSize: '18px', fontWeight: 700 }}>
          {FLAGS[radar.country_code] || '🌍'} {radar.name}
        </span>
        <span style={{ fontSize: '12px', color: 'var(--text-muted)' }}>
          {t('radar.radius')}: {radar.radius_km}km
        </span>
      </div>
      <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '16px' }}>
        <RadarMap data={detail} locale={locale} />
        <RadarEventList detail={detail} locale={locale} t={t} />
      </div>
    </div>
  );
}

export function CityRadarTab() {
  const { isAdmin } = useAuth();
  // Admins get the full management UI (select city, scan, IG focus, sources,
  // add city). Everyone else gets a READ-ONLY view of the active city's events
  // + map (user rule 2026-06-11: any user can see the events; only admin
  // selects/manages). WipBanner is retired now that the public view is real.
  return isAdmin ? <CityRadarAdmin /> : <CityRadarPublic />;
}

/**
 * Add-city form (admin): name + country are enough — the server geocodes via
 * Nominatim and kicks the web source discovery in the background. Keywords
 * are the city's local scene vocabulary (they also become IG search queries
 * once the city gets the discovery focus).
 */
function AddCityForm({ onCreated }: { onCreated: () => void }) {
  const { t } = useLang();
  const [open, setOpen] = useState(false);
  const [name, setName] = useState('');
  const [country, setCountry] = useState('');
  const [language, setLanguage] = useState('en');
  const [keywords, setKeywords] = useState('');
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  async function submit(e: React.FormEvent) {
    e.preventDefault();
    if (busy) return;
    setBusy(true);
    setError(null);
    try {
      await api.createRadar({
        name: name.trim(),
        country_code: country.trim().toUpperCase(),
        language: language.trim().toLowerCase(),
        keywords: keywords.split(',').map(k => k.trim()).filter(Boolean),
      });
      setName(''); setCountry(''); setKeywords(''); setOpen(false);
      onCreated();
    } catch (err: any) {
      setError(err.message || t('radar.create_error'));
    } finally {
      setBusy(false);
    }
  }

  if (!open) {
    return (
      <button
        onClick={() => setOpen(true)}
        style={{
          background: 'var(--bg-card)', border: `1px dashed ${GOLD}`, borderRadius: '10px',
          padding: '14px 20px', cursor: 'pointer', minWidth: '140px', textAlign: 'center',
        }}
      >
        <div style={{ fontSize: '28px', marginBottom: '4px', color: GOLD }}>＋</div>
        <div style={{ fontWeight: 700, fontSize: '13px', color: GOLD }}>{t('radar.add_city')}</div>
      </button>
    );
  }

  const inputStyle: React.CSSProperties = {
    background: 'rgba(255,255,255,0.04)', border: '1px solid var(--border-color)',
    borderRadius: '6px', padding: '8px 10px', color: 'var(--text-primary)', fontSize: '13px',
  };

  return (
    <form
      onSubmit={submit}
      style={{
        background: 'var(--bg-card)', border: `1px solid ${GOLD}`, borderRadius: '10px',
        padding: '14px 16px', display: 'flex', flexDirection: 'column', gap: '8px', minWidth: '280px',
      }}
    >
      <input style={inputStyle} placeholder={t('radar.city_name')} value={name}
        onChange={e => setName(e.target.value)} required minLength={2} maxLength={40} />
      <input style={inputStyle} placeholder={t('radar.country')} value={country}
        onChange={e => setCountry(e.target.value)} required pattern="[A-Za-z]{2}" maxLength={2} />
      <input style={inputStyle} placeholder={t('radar.language')} value={language}
        onChange={e => setLanguage(e.target.value)} pattern="[a-z]{2}" maxLength={2} />
      <input style={inputStyle} placeholder={t('radar.keywords')} value={keywords}
        onChange={e => setKeywords(e.target.value)} />
      {error && <div style={{ color: RED, fontSize: '11px' }}>{error}</div>}
      <div style={{ display: 'flex', gap: '8px' }}>
        <button type="submit" className="btn btn-primary" disabled={busy} style={{ flex: 1 }}>
          {busy ? t('radar.creating') : t('radar.create')}
        </button>
        <button type="button" className="btn" onClick={() => setOpen(false)} disabled={busy}>✕</button>
      </div>
    </form>
  );
}

function CityRadarAdmin() {
  const { t, lang } = useLang();
  const [radars, setRadars] = useState<CityRadar[]>([]);
  const [selectedId, setSelectedId] = useState<string | null>(null);
  const [radarDetail, setRadarDetail] = useState<any>(null);
  const [loading, setLoading] = useState(true);
  const [scanning, setScanning] = useState(false);
  const [togglingFocus, setTogglingFocus] = useState(false);

  const locale = lang === 'nl' ? 'nl-BE' : lang === 'en' ? 'en-GB' : 'fr-BE';

  useEffect(() => { loadRadars(); }, []);

  async function loadRadars() {
    setLoading(true);
    try {
      setRadars(await api.getRadars());
    } catch (err) {
      console.error('Failed to load radars:', err);
    } finally {
      setLoading(false);
    }
  }

  async function selectRadar(id: string) {
    setSelectedId(id);
    try {
      setRadarDetail(await api.getRadar(id));
    } catch (err) {
      console.error('Failed to load radar:', err);
    }
  }

  async function handleScan() {
    if (!selectedId || scanning) return;
    setScanning(true);
    try {
      await api.scanRadar(selectedId);
      // Reload data
      await loadRadars();
      setRadarDetail(await api.getRadar(selectedId));
    } catch (err) {
      console.error('Scan failed:', err);
    } finally {
      setScanning(false);
    }
  }

  // Toggle the single IG-discovery focus slot. The server clears the flag on
  // every other radar when enabling — one featured city at a time.
  async function handleToggleFocus(radar: CityRadar) {
    if (togglingFocus) return;
    setTogglingFocus(true);
    try {
      await api.updateRadar(radar.id, { ig_discovery_enabled: radar.ig_discovery_enabled ? 0 : 1 });
      await loadRadars();
    } catch (err) {
      console.error('IG focus toggle failed:', err);
    } finally {
      setTogglingFocus(false);
    }
  }

  const formatDate = (d: string) => {
    const date = new Date(d);
    if (isNaN(date.getTime())) return d;
    return date.toLocaleDateString(locale, { day: 'numeric', month: 'short', year: 'numeric' });
  };

  const selected = radars.find(r => r.id === selectedId);
  // Cooldown is GLOBAL across all cities — gates every city's scan button.
  const cooldown = globalScanCooldown(radars);

  return (
    <div>
      {/* Header */}
      <div className="section-header">
        <span className="section-title">{t('radar.title')}</span>
      </div>

      {/* City cards */}
      <div style={{ display: 'flex', gap: '12px', marginBottom: '20px', flexWrap: 'wrap' }}>
        {loading ? (
          <div className="loading"><span className="spinner"></span></div>
        ) : radars.map(r => (
          <button
            key={r.id}
            onClick={() => selectRadar(r.id)}
            style={{
              background: selectedId === r.id ? 'rgba(201,168,76,0.15)' : 'var(--bg-card)',
              border: `1px solid ${selectedId === r.id ? GOLD : 'var(--border-color)'}`,
              borderRadius: '10px',
              padding: '14px 20px',
              cursor: 'pointer',
              minWidth: '140px',
              textAlign: 'center',
              transition: 'all 0.2s',
            }}
          >
            <div style={{ fontSize: '28px', marginBottom: '4px' }}>{FLAGS[r.country_code] || '🌍'}</div>
            <div style={{ fontWeight: 700, fontSize: '15px', color: 'var(--text-primary)', marginBottom: '2px' }}>
              {r.name}
              {!!r.ig_discovery_enabled && (
                <span title={t('radar.ig_focus')} style={{ marginLeft: '6px', fontSize: '12px' }}>📡</span>
              )}
            </div>
            <div style={{ fontSize: '11px', color: 'var(--text-muted)', fontFamily: 'JetBrains Mono' }}>
              {r.event_count || 0} events
            </div>
          </button>
        ))}
        {!loading && <AddCityForm onCreated={loadRadars} />}
      </div>

      {/* Selected city dashboard */}
      {selected && (
        <div>
          {/* City header bar */}
          <div style={{
            display: 'flex', justifyContent: 'space-between', alignItems: 'center',
            padding: '12px 16px', marginBottom: '16px',
            background: 'var(--bg-card)', border: '1px solid var(--border-color)', borderRadius: '8px',
          }}>
            <div>
              <span style={{ fontSize: '18px', fontWeight: 700 }}>
                {FLAGS[selected.country_code]} {selected.name}
              </span>
              <span style={{ fontSize: '12px', color: 'var(--text-muted)', marginLeft: '12px' }}>
                {t('radar.radius')}: {selected.radius_km}km
              </span>
              {selected.last_scanned && (
                <span style={{ fontSize: '11px', color: 'var(--text-muted)', marginLeft: '12px' }}>
                  {t('radar.last_scan')}: {formatDate(selected.last_scanned)}
                </span>
              )}
            </div>
            <div style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
              <button
                onClick={() => handleToggleFocus(selected)}
                disabled={togglingFocus}
                title={t('radar.ig_focus_hint')}
                style={{
                  background: selected.ig_discovery_enabled ? 'rgba(201,168,76,0.18)' : 'transparent',
                  border: `1px solid ${selected.ig_discovery_enabled ? GOLD : 'var(--border-color)'}`,
                  color: selected.ig_discovery_enabled ? GOLD : 'var(--text-muted)',
                  borderRadius: '6px', padding: '6px 12px', cursor: 'pointer',
                  fontSize: '11px', fontWeight: 700, fontFamily: 'JetBrains Mono',
                }}
              >
                📡 {t('radar.ig_focus')} {selected.ig_discovery_enabled ? 'ON' : 'OFF'}
              </button>
              <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'flex-end', gap: '3px' }}>
                <button
                  className="btn btn-primary"
                  onClick={handleScan}
                  disabled={scanning || cooldown.cooling}
                  title={cooldown.cooling ? `${t('radar.next_scan_in')} ${cooldown.hoursLeft}h` : undefined}
                  style={{ minWidth: '100px', opacity: cooldown.cooling ? 0.55 : 1 }}
                >
                  {scanning ? t('radar.scanning') : t('radar.scan_now')}
                </button>
                {cooldown.cooling && (
                  <span style={{ fontSize: '10px', color: 'var(--text-muted)', fontFamily: 'JetBrains Mono' }}>
                    🕒 {t('radar.next_scan_in')} {cooldown.hoursLeft}h
                  </span>
                )}
              </div>
            </div>
          </div>

          {/* Main content: map + events (shared renderers) */}
          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '16px', marginBottom: '20px' }}>
            <RadarMap data={radarDetail} locale={locale} />
            <RadarEventList detail={radarDetail} locale={locale} t={t} />
          </div>
          {/* Sources */}
          {radarDetail && (
            <div style={{
              background: 'var(--bg-card)', border: '1px solid var(--border-color)', borderRadius: '8px',
              padding: '16px',
            }}>
              <div style={{
                fontSize: '12px', fontWeight: 700, color: GOLD, textTransform: 'uppercase',
                letterSpacing: '1px', marginBottom: '10px',
              }}>
                {t('radar.sources')} ({radarDetail.sources?.length || 0})
              </div>
              <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr 1fr', gap: '6px' }}>
                {(radarDetail.sources || []).map((s: any) => (
                  <a key={s.id} href={s.url} target="_blank" rel="noopener noreferrer" style={{
                    padding: '6px 10px', borderRadius: '6px', fontSize: '11px',
                    background: 'rgba(255,255,255,0.03)', border: '1px solid var(--border-color)',
                    textDecoration: 'none', display: 'flex', alignItems: 'center', gap: '6px',
                  }}>
                    <span style={{
                      fontSize: '9px', padding: '1px 5px', borderRadius: '3px', fontWeight: 700,
                      background: s.type === 'instagram' ? 'rgba(225,48,108,0.15)' : s.type === 'facebook' ? 'rgba(66,103,178,0.15)' : 'rgba(76,175,80,0.15)',
                      color: s.type === 'instagram' ? '#e1306c' : s.type === 'facebook' ? '#4267B2' : G,
                      textTransform: 'uppercase',
                    }}>
                      {s.type === 'instagram' ? 'IG' : s.type === 'facebook' ? 'FB' : 'WEB'}
                    </span>
                    <span style={{ fontWeight: 600, color: 'var(--text-primary)', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                      {s.name.replace(' (Instagram)', '').replace(' [auto]', '')}
                    </span>
                  </a>
                ))}
              </div>
              {(!radarDetail.sources || radarDetail.sources.length === 0) && (
                <div style={{ color: 'var(--text-muted)', fontSize: '12px', fontStyle: 'italic' }}>
                  {t('radar.never_scanned')}
                </div>
              )}
            </div>
          )}
        </div>
      )}

      {/* No city selected */}
      {!selected && !loading && (
        <div className="empty-state" style={{ padding: '40px' }}>
          <p style={{ fontSize: '14px', color: 'var(--text-muted)' }}>{t('radar.select_city')}</p>
        </div>
      )}
    </div>
  );
}
